'use client'

import { useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, Bubble, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { ConversationMessage } from '@marketplace-web/messaging/conversation-messaging-data'
import {
  OfferMessage as OfferMessageType,
  buyerBuyEvent,
} from '@marketplace-web/offers/price-offers-data'
import { formatCurrency } from '@marketplace-web/currency/currency-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  GenericErrorModal,
  useNavigateToCheckout,
} from '@marketplace-web/checkout/checkout-feature'
import {
  CheckoutOrderTypeMap,
  ExtraServiceOrderType,
} from '@marketplace-web/checkout/checkout-data'
import { UserPhoto } from '@marketplace-web/messaging/conversation-user-feature'

import { MEMBER_PROFILE_URL } from '../../constants/routes'
import MessageTime from '../MessageTime'
import { BuyButtonPosition } from '../constants'
import { isOfferMessage } from '../../utils/price-offers'
import { isDateInTheFuture } from '../../utils/date'

type OwnProps = {
  message: OfferMessageType
}

type Props = OwnProps & ConversationMessage

const OfferMessage = ({ message, createdTime }: Props) => {
  const translate = useTranslate('conversation')
  const { track } = useTracking()

  const { navigateToCheckout } = useNavigateToCheckout()

  const [isGenericErrorShown, setIsGenericErrorShown] = useState(false)
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)

  const currentUserId = useSession().user?.id
  const { conversation, transaction } = useConversationContext()
  const oppositeUser = conversation?.oppositeUser
  const { locale } = useIntl()

  const isCurrentAuthor = message.userId === String(currentUserId)
  const { price, originalPrice, currency } = message
  const priceValue = typeof price === 'string' ? Number(price) : price.amount
  const originalPriceValue =
    typeof originalPrice === 'string' ? Number(originalPrice) : originalPrice.amount

  const isBuyButtonShown = () => {
    const showPaymentsButtons = conversation?.isPaymentActionAvailable
    const isBuyer = conversation?.buyerId === currentUserId

    const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)

    return message.isCurrentOffer && showPaymentsButtons && isBuyer && !isSellerCountdownActive
  }

  const handleBuyButtonClick = async () => {
    setIsCheckoutLoading(true)
    if (!conversation?.transaction?.itemId) return
    if (!conversation?.transaction?.id) return

    const sellerOfferId = message.isCurrentOffer && isOfferMessage(message) ? message.id : undefined

    track(
      buyerBuyEvent({
        transactionId: conversation.transaction.id,
        screen: 'message_reply',
        buttonPosition: BuyButtonPosition.ConversationMessageOffer,
        ...(sellerOfferId && { sellerOfferId: Number(sellerOfferId) }),
      }),
    )

    if (!conversation?.transactionId) return

    const isError = await navigateToCheckout(
      conversation.transactionId,
      CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction],
    )

    if (isError) {
      setIsGenericErrorShown(true)
      setIsCheckoutLoading(false)
    }
  }

  const handleCloseGenericErrorModal = () => {
    setIsGenericErrorShown(false)
  }

  const renderPrices = () => {
    return (
      <div>
        <Text
          as="h2"
          text={formatCurrency(priceValue, { currency, locale })}
          type={Text.Type.Title}
          testId="offer-current-price-label"
        />
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="h2"
          text={formatCurrency(originalPriceValue, { currency, locale })}
          type={Text.Type.Title}
          theme="muted"
          strikethrough
          testId="offer-original-price-label"
        />
      </div>
    )
  }

  const renderNote = () => {
    if (!message.note) return null

    return <Text as="span" text={message.note} width={Text.Width.Parent} />
  }

  const renderCurrencyExchangeRateNote = () => {
    if (!conversation?.transactionId) return null
    if (!isBuyButtonShown()) return null
    if (message.isPriceFixed) return null

    return (
      <>
        <Text as="h4" type={Text.Type.Caption} text={translate('currency_exchange_rate_note')} />
        <Spacer />
      </>
    )
  }

  const renderBuyButton = () => {
    if (!conversation?.transactionId || isBuyButtonShown() === false) return null

    return (
      <>
        <Spacer />
        <Button
          text={translate('offer_message.buy')}
          isLoading={isCheckoutLoading}
          size={Button.Size.Medium}
          styling={Button.Styling.Filled}
          onClick={handleBuyButtonClick}
          testId="offer-message-buy-button"
        />
      </>
    )
  }

  const renderUserPhoto = () => {
    if (!oppositeUser) return null

    const { photo, id } = oppositeUser

    return (
      <div className="u-ui-margin-right-regular u-inline-block u-float-left">
        <UserPhoto
          photo={photo}
          profileUrl={MEMBER_PROFILE_URL(id)}
          isSystem={false}
          isCurrentAuthor={isCurrentAuthor}
        />
      </div>
    )
  }

  if (!currentUserId || !conversation || !oppositeUser) return null

  const messageClass = classNames('conversation-message', {
    'conversation-message__current-author': isCurrentAuthor,
    'conversation-message__single-action': isBuyButtonShown(),
  })

  return (
    <>
      <MessageTime time={createdTime} />
      <div className="conversation-message-container u-ui-margin-top-medium u-fill-width u-inline-block">
        {renderUserPhoto()}
        <div className={messageClass}>
          <Bubble inverse={isCurrentAuthor}>
            {renderCurrencyExchangeRateNote()}
            {renderPrices()}
            {renderNote()}
            {renderBuyButton()}
          </Bubble>
        </div>
      </div>
      {isGenericErrorShown && (
        <GenericErrorModal isShown={isGenericErrorShown} onClose={handleCloseGenericErrorModal} />
      )}
    </>
  )
}

export default OfferMessage
